// src/components/admin/ProfileForm.js

import React, { useEffect, useMemo } from 'react';
import { Modal, Form, Input, Switch, Row, Col, Typography, Divider, message, Collapse, Space, Tooltip } from 'antd';
import {
    CarOutlined,
    UnorderedListOutlined,
    TeamOutlined,
    AppstoreOutlined,
    EyeOutlined,
    DeleteRowOutlined,
    ApartmentOutlined,
    MessageOutlined,
    DeleteFilled,
    ToolOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

const permissionLabels = {
    pages: { 
        title: "Acceso a Módulos y Pantallas", 
        icon: <EyeOutlined />,
        viewDeliveries: "Ver Módulo de Entregas",
        viewDeletionManagement: "Ver Módulo de Gestión de Eliminaciones",
        viewProntoPago: "Ver Módulo de Cobranza · Pronto Pago (solo Tecno)",
        viewAdminPanel: {
            label: "Ver Menú 'Administración'",
            children: {
                viewAdminUsers: "Ver Gestión de Acceso",
                viewAdminCatalogs: {
                    label: "Ver Submenú 'Catálogos'",
                    children: {
                        viewAdminPallets: "Ver Catálogo de Tarimas",
                        viewClientCodes: "Ver Catálogo de Códigos Cliente",
                        viewAdminDeletionReasons: "Ver Catálogo de Motivos de Eliminación",
                    }
                },
                viewAdminConfig: "Ver Configuración General",
            }
        }
    },
    actions: { 
        title: "Permisos de Acciones Específicas", 
        deliveries: { 
            title: "En Pantalla de Entregas", 
            icon: <CarOutlined />,
            __master__: "Todos",
            canSearch: "Puede usar el botón 'Buscar'", 
            canIncludeClosed: "Puede 'Incluir Cerradas'",
            onOpen: {
                title: "En Entregas ABIERTAS",
                canCreatePackingList: "Puede 'Crear Empaque'", 
                canEditPackingList: "Puede 'Editar LE'", 
                canDeletePackingList: "Puede 'Eliminar LE' (Directo)",
                canRequestPackingListDeletion: "Puede 'Solicitar Eliminación'"
            },
            onClosed: {
                title: "En Entregas CERRADAS",
                canCreatePackingList: "Puede 'Crear Empaque'", 
                canEditPackingList: "Puede 'Editar LE'", 
                canDeletePackingList: "Puede 'Eliminar LE' (Directo)",
                canRequestPackingListDeletion: "Puede 'Solicitar Eliminación'"
            }
        },
        
        packingListEditor: { 
            title: "En Editor Listado Formato A", 
            icon: <UnorderedListOutlined />,
            __master__: "Todos",
            onOpen: {
                title: "En Entregas ABIERTAS",
                canCreate: "Guardar Nuevo Listado",
                canManageEvidenceOnCreate: "Gestionar Evidencias (al Crear)",
                canEdit: "Guardar Cambios (en Edición)",
                canManageEvidenceOnEdit: "Gestionar Evidencias (en Edición)",
                canViewEvidence: "Ver Pestaña Evidencias (en Edición)",
                canDeleteEvidence: "Puede Eliminar Evidencias(en Edición)",
                canGeneratePdf: "Generar PDF (en Edición)",
            },
            onClosed: {
                title: "En Entregas CERRADAS",
                canCreate: "Guardar Nuevo Listado",
                canManageEvidenceOnCreate: "Gestionar Evidencias (al Crear)",
                canEdit: "Guardar Cambios (en Edición)",
                canManageEvidenceOnEdit: "Gestionar Evidencias (en Edición)",
                canViewEvidence: "Ver Pestaña Evidencias (en Edición)",
                canDeleteEvidence: "Puede Eliminar Evidencias(en Edición)",
                canGeneratePdf: "Generar PDF (en Edición)",
            }
        }, 
        packingListEditorPfs: { 
            title: "En Editor Listado Formato B", 
            icon: <UnorderedListOutlined style={{ color: '#52c41a' }}/>,
            __master__: "Todos",
            onOpen: {
                title: "En Entregas ABIERTAS",
                canCreate: "Guardar Nuevo Listado",
                canManageEvidenceOnCreate: "Gestionar Evidencias (al Crear)",
                canEdit: "Guardar Cambios (en Edición)",
                canManageEvidenceOnEdit: "Gestionar Evidencias (en Edición)",
                canViewEvidence: "Ver Pestaña Evidencias (en Edición)",
                canDeleteEvidence: "Puede Eliminar Evidencias(en Edición)",
                canGeneratePdf: "Generar PDF (en Edición)",
            },
            onClosed: {
                title: "En Entregas CERRADAS",
                canCreate: "Guardar Nuevo Listado",
                canManageEvidenceOnCreate: "Gestionar Evidencias (al Crear)",
                canEdit: "Guardar Cambios (en Edición)",
                canManageEvidenceOnEdit: "Gestionar Evidencias (en Edición)",
                canViewEvidence: "Ver Pestaña Evidencias (en Edición)",
                canDeleteEvidence: "Puede Eliminar Evidencias(en Edición)",
                canGeneratePdf: "Generar PDF (en Edición)",
            }
        },
   
        deletionManagement: {
            title: "En Gestión de Eliminaciones",
            icon: <DeleteRowOutlined />,
            __master__: "Todos",
            canApproveRequest: "Puede 'Aprobar' solicitudes",
            canRejectRequest: "Puede 'Rechazar' solicitudes",
            canViewDeletionLog: "Puede ver el 'Historial de Eliminaciones'",
            canViewRecycleBin: "Puede ver la 'Papelera' de eliminados",
            canRestoreRequest: "Puede 'Restaurar' desde la papelera",
            canPermanentlyDelete: "Puede 'Eliminar Definitivamente' desde la papelera",
        },
        
        userManagement: { 
            title: "En Gestión de Usuarios y Permisos", 
            icon: <TeamOutlined />,
            __master__: "Todos",
            canCreateUser: "Puede crear usuarios", 
            canEditUser: "Puede editar usuarios", 
            canDeleteUser: "Puede eliminar usuarios", 
            canChangeUserPassword: "Puede cambiar contraseña de usuarios",
            canCreateRole: "Puede crear roles", 
            canEditRole: "Puede editar roles", 
            canDeleteRole: "Puede eliminar roles", 
            canCreateProfile: "Puede crear perfiles", 
            canEditProfile: "Puede editar perfiles", 
            canDeleteProfile: "Puede eliminar perfiles" 
        }, 
        palletCatalog: { 
            title: "En Catálogo de Tarimas", 
            icon: <AppstoreOutlined />,
            __master__: "Todos",
            canCreatePallet: "Puede crear tarimas", 
            canEditPallet: "Puede editar tarimas", 
            canDeletePallet: "Puede eliminar tarimas" 
        },   
        clientCodes: {
            title: "En Catálogo de Códigos Cliente",
            icon: <ApartmentOutlined />,
            __master__: "Todos",
            canCreate: "Puede crear equivalencias (manual)",
            canEdit: "Puede editar equivalencias",
            canDelete: "Puede eliminar equivalencias",
            canUpload: "Puede realizar carga masiva (Excel)"
        },
        deletionReasonCatalog: {
            title: "En Catálogo de Motivos de Eliminación",
            icon: <MessageOutlined />,
            __master__: "Todos",
            canCreateReason: "Puede crear motivos",
            canEditReason: "Puede editar motivos",
            canDeleteReason: "Puede eliminar motivos"
        },   
        settings: {
            title: "En Módulo de Configuración",
            icon: <ToolOutlined />,
            __master__: "Todos",
            canViewSettings: "Puede ver la página de configuración",
            canEditSettings: "Puede editar y guardar los cambios"
        }
        
    }
};


const MasterSwitch = ({ form, parentKey }) => {
    const permissions = Form.useWatch('permissions', form) || {};
    
    const childPaths = useMemo(() => {
        let section = permissionLabels;
        for (const key of parentKey) {
            section = section?.[key];
        }

        const getPaths = (perms, basePath) => {
            if (!perms) return [];
            let paths = [];
            Object.keys(perms).forEach(key => {
                if (key.startsWith('_') || key === 'title' || key === 'icon' || key === 'label' || key === 'children') return;
                const currentItem = perms[key];
                const newPath = [...basePath, key];
                if (typeof currentItem === 'object' && currentItem !== null && !React.isValidElement(currentItem)) {
                    paths = paths.concat(getPaths(currentItem.children || currentItem, newPath));
                } else {
                    paths.push(['permissions', ...newPath]);
                }
            });
            return paths;
        };
        return getPaths(section, parentKey);
    }, [parentKey]);

    const allChecked = useMemo(() => {
        if (!childPaths.length) return false;
        return childPaths.every(path => {
            let value = permissions;
            for (let i = 1; i < path.length; i++) {
                value = value?.[path[i]];
            }
            return !!value;
        });
    }, [permissions, childPaths]);

    const handleMasterChange = (checked) => {
        const fieldsToUpdate = childPaths.map(path => ({
            name: path,
            value: checked
        }));
        form.setFields(fieldsToUpdate);
    };

    let section = permissionLabels;
    parentKey.forEach(key => section = section[key]);
    const label = section.__master__ || 'Todos';

    return (
        <Tooltip title={allChecked ? `Deseleccionar ${label.toLowerCase()}` : `Seleccionar ${label.toLowerCase()}`}>
            <Switch checked={allChecked} onChange={handleMasterChange} />
            <label style={{ marginLeft: 8, fontWeight: 'bold' }}>{label}</label>
        </Tooltip>
    );
};

const ProfileForm = ({ open, onCancel, onFinish, initialValues, submitting }) => {
    const [form] = Form.useForm();
    const isEditing = !!initialValues;

    useEffect(() => {
        if (open) {
            if (isEditing && initialValues) {
                form.setFieldsValue({
                    ...initialValues,
                    permissions: initialValues.permissions || {},
                });
            } else {
                form.resetFields();
            }
        }
    }, [open, initialValues, form, isEditing]);

    const handleOk = () => {
        form.validateFields()
            .then(currentValues => {
                if (isEditing) {
                    const deepMerge = (target, source) => {
                        const output = { ...target };
                        for (const key in source) {
                            if (Object.prototype.hasOwnProperty.call(source, key)) {
                                const sourceValue = source[key];
                                const targetValue = target[key];
                                if (
                                    sourceValue && typeof sourceValue === 'object' && !Array.isArray(sourceValue) &&
                                    targetValue && typeof targetValue === 'object' && !Array.isArray(targetValue)
                                ) {
                                    output[key] = deepMerge(targetValue, sourceValue);
                                } else {
                                    output[key] = sourceValue;
                                }
                            }
                        }
                        return output;
                    };

                    const finalPayload = {
                        ...initialValues,
                        name: currentValues.name,
                        permissions: deepMerge(initialValues.permissions || {}, currentValues.permissions || {})
                    };
                    onFinish(finalPayload);
                } else {
                    onFinish(currentValues);
                }
            })
            .catch(info => {
                message.warning('Por favor, complete todos los campos requeridos.');
                console.log('Validate Failed:', info);
            });
    };
    
    const renderPageAccessPermissions = (labels, parentDataPath = [], level = 0) => {
        return Object.keys(labels).map(key => {
            if (key === 'title' || key === 'icon') return null;

            const currentConfig = labels[key];
            const currentDataPath = [...parentDataPath, key];
            
            if (typeof currentConfig === 'object' && currentConfig !== null && currentConfig.label) {
                return (
                    <React.Fragment key={key}>
                        <Col span={24} style={{ paddingLeft: level * 24, marginTop: '8px' }}>
                            <Form.Item name={['permissions', ...currentDataPath]} valuePropName="checked" noStyle><Switch /></Form.Item>
                            <label style={{ marginLeft: 8, fontWeight: 500 }}>{currentConfig.label}</label>
                        </Col>
                        {currentConfig.children && renderPageAccessPermissions(currentConfig.children, parentDataPath, level + 1)}
                    </React.Fragment>
                );
            }
            
            if (typeof currentConfig === 'string') {
                return (
                    <Col span={24} style={{ paddingLeft: level * 24, marginTop: '8px' }} key={key}>
                       <Form.Item name={['permissions', ...currentDataPath]} valuePropName="checked" noStyle><Switch /></Form.Item>
                       <label style={{ marginLeft: 8 }}>{currentConfig}</label>
                    </Col>
                );
            }

            return null;
        });
    };

    const renderActionGridPermissions = (labels, parentKey = []) => {
        return Object.keys(labels).map(key => {
            if (key === 'title' || key === 'icon') return null;

            const currentConfig = labels[key];
            const newParentKey = [...parentKey, key];

            if (key === '__master__') {
                return (
                    <Col span={24} key={key} style={{ marginBottom: 16 }}>
                        <MasterSwitch parentKey={parentKey} form={form} />
                        <Divider style={{ marginTop: 16, marginBottom: 0 }}/>
                    </Col>
                );
            }

            if (typeof currentConfig === 'object' && !React.isValidElement(currentConfig)) {
                return (
                     <Col span={24} key={key}>
                        <Divider orientation="left" plain>
                            <Text strong>{currentConfig.title}</Text>
                        </Divider>
                        <Row gutter={[16, 24]} style={{ paddingLeft: '16px' }}>
                            {renderActionGridPermissions(currentConfig, newParentKey)}
                        </Row>
                     </Col>
                );
            }

            return (
                <Col xs={24} sm={12} md={12} lg={8} key={key}>
                   <Form.Item name={['permissions', ...newParentKey]} valuePropName="checked" noStyle><Switch /></Form.Item>
                   <label style={{ marginLeft: 8 }}>{currentConfig}</label>
                </Col>
            )
        });
    };

    const pageItems = useMemo(() => [{
        key: 'pages',
        label: <Text strong><Space>{permissionLabels.pages.icon} {permissionLabels.pages.title}</Space></Text>,
        children: (
            <div style={{padding: "0 16px"}}>
                {renderPageAccessPermissions(permissionLabels.pages, ['pages'])}
            </div>
        )
    }], []);

    const actionItems = useMemo(() => Object.keys(permissionLabels.actions).map(key => {
            if (key === 'title') return null;
            const section = permissionLabels.actions[key];
            return {
                key: key,
                label: <Text strong><Space>{section.icon} {section.title}</Space></Text>,
                children: (
                    <Row gutter={[16, 24]}>
                        {renderActionGridPermissions(section, ['actions', key])}
                    </Row>
                ),
            };
        }).filter(Boolean), 
        []
    );

    return (
        <Modal
            title={isEditing ? 'Editar Perfil' : 'Crear Perfil'}
            open={open}
            onOk={handleOk}
            onCancel={onCancel}
            okButtonProps={{ loading: submitting }}
            okText={isEditing ? 'Guardar Cambios' : 'Crear'}
            cancelText="Cancelar"
            width={1200}
            destroyOnClose
        >
            <Form form={form} layout="vertical" name="profile_form" initialValues={{ permissions: {} }}>
                <Form.Item name="name" label="Nombre del Perfil" rules={[{ required: true, message: 'Por favor, ingrese un nombre para el perfil.' }]}>
                    <Input />
                </Form.Item>
                <Divider>Permisos</Divider>
                
                <Collapse accordion style={{ width: '100%' }} items={pageItems} />

                <Title level={5} style={{marginTop: 24}}>Permisos de Acciones Específicas</Title>
                <Collapse accordion style={{ width: '100%' }} items={actionItems} />

            </Form>
        </Modal>
    );
};

export default ProfileForm;
